import React from 'react';
import { ChemicalType, DailyReportRow, PreviousDayStock } from '../types';
import { CHEMICALS } from '../constants/chemicals';
import { ArrowDownLeft, ArrowUpRight, History, PackageCheck, Scale } from 'lucide-react';

interface SummaryCardsProps {
  chemical: ChemicalType | 'all';
  rows: DailyReportRow[];
  previousDayStock: PreviousDayStock;
}

export const SummaryCards: React.FC<SummaryCardsProps> = ({
  chemical,
  rows,
  previousDayStock,
}) => {
  const formatNum = (val: number | null | undefined, decimals = 3) => {
    if (val === null || val === undefined || isNaN(val)) return '-';
    return Number(val).toLocaleString('th-TH', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  if (chemical === 'all') {
    // Show quick totals per chemical
    const chems: ChemicalType[] = ['alum', 'pac', 'chl', 'poly'];
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {chems.map((c) => {
          const meta = CHEMICALS[c];
          const prevStock = previousDayStock[c] ?? 0;
          const totalIncome = rows.reduce((sum, r) => sum + (r[c].income ?? 0), 0);
          const totalUsage = rows.reduce((sum, r) => sum + (r[c].usage ?? 0), 0);
          const lastRow = rows.length > 0 ? rows[rows.length - 1] : null;
          const closingStock = lastRow ? lastRow[c].stock : null;

          return (
            <div
              key={c}
              className="bg-white rounded-xl p-4 border border-slate-200 shadow-sm flex flex-col justify-between"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-bold text-slate-800">{meta.nameEn}</span>
                <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${meta.color.badge}`}>
                  {meta.nameTh}
                </span>
              </div>

              <div className="space-y-2 text-xs">
                <div className="flex justify-between items-center text-slate-500 bg-amber-50/60 p-1.5 rounded">
                  <span>Stock วันก่อนหน้า:</span>
                  <span className="font-semibold text-amber-700">{formatNum(prevStock)} ตัน</span>
                </div>
                <div className="flex justify-between items-center text-slate-600">
                  <span>รับรวม:</span>
                  <span className="font-semibold text-emerald-600">+{formatNum(totalIncome)} ตัน</span>
                </div>
                <div className="flex justify-between items-center text-slate-600">
                  <span>ใช้รวม:</span>
                  <span className="font-semibold text-rose-600">-{formatNum(totalUsage)} ตัน</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-slate-100 text-slate-800 font-medium">
                  <span>Stock ล่าสุด:</span>
                  <span className="font-bold text-blue-700">{formatNum(closingStock)} ตัน</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  const prevStock = previousDayStock[chemical];
  const totalIncome = rows.reduce((sum, r) => sum + (r[chemical].income ?? 0), 0);
  const totalUsage = rows.reduce((sum, r) => sum + (r[chemical].usage ?? 0), 0);

  const lastRow = rows.length > 0 ? rows[rows.length - 1] : null;
  const closingStock = lastRow ? lastRow[chemical].stock : null;

  const calculatedBalance =
    prevStock !== null && prevStock !== undefined
      ? prevStock + totalIncome - totalUsage
      : null;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Stock วันก่อนหน้า (ยอดยกมา) */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50/50 rounded-xl p-4 border border-amber-200/80 shadow-xs relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-amber-800">
              Stock วันก่อนหน้า (ยอดยกมา)
            </span>
            <History className="w-5 h-5 text-amber-600" />
          </div>
          <div className="mt-2 flex items-baseline gap-1">
            <span className="text-2xl font-bold text-amber-900 tracking-tight">
              {formatNum(prevStock)}
            </span>
            <span className="text-xs font-medium text-amber-700">ตัน</span>
          </div>
          <div className="mt-1 text-xs text-amber-600 flex items-center gap-1">
            <span>ณ สิ้นวัน: {previousDayStock.date || 'ก่อนวันเริ่มต้น'}</span>
          </div>
        </div>

        {/* Card 2: ปริมาณการรับรวม */}
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50/50 rounded-xl p-4 border border-emerald-200/80 shadow-xs relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-emerald-800">
              ปริมาณการรับรวม (Income)
            </span>
            <ArrowDownLeft className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="mt-2 flex items-baseline gap-1">
            <span className="text-2xl font-bold text-emerald-900 tracking-tight">
              {formatNum(totalIncome)}
            </span>
            <span className="text-xs font-medium text-emerald-700">ตัน</span>
          </div>
          <div className="mt-1 text-xs text-emerald-600">
            จำนวนวันที่มีการรับ: {rows.filter((r) => (r[chemical].income ?? 0) > 0).length} วัน
          </div>
        </div>

        {/* Card 3: ปริมาณการใช้รวม */}
        <div className="bg-gradient-to-br from-rose-50 to-red-50/50 rounded-xl p-4 border border-rose-200/80 shadow-xs relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-rose-800">
              ปริมาณการใช้รวม (Usage)
            </span>
            <ArrowUpRight className="w-5 h-5 text-rose-600" />
          </div>
          <div className="mt-2 flex items-baseline gap-1">
            <span className="text-2xl font-bold text-rose-900 tracking-tight">
              {formatNum(totalUsage)}
            </span>
            <span className="text-xs font-medium text-rose-700">ตัน</span>
          </div>
          <div className="mt-1 text-xs text-rose-600">
            เฉลี่ยวันละ:{' '}
            {rows.length > 0 ? formatNum(totalUsage / rows.length, 3) : 0} ตัน/วัน
          </div>
        </div>

        {/* Card 4: ปริมาณ Stock คงเหลือล่าสุด */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50/50 rounded-xl p-4 border border-blue-200/80 shadow-xs relative overflow-hidden">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-blue-800">
              Stock คงเหลือล่าสุด (24:00)
            </span>
            <PackageCheck className="w-5 h-5 text-blue-600" />
          </div>
          <div className="mt-2 flex items-baseline gap-1">
            <span className="text-2xl font-bold text-blue-900 tracking-tight">
              {formatNum(closingStock)}
            </span>
            <span className="text-xs font-medium text-blue-700">ตัน</span>
          </div>
          <div className="mt-1 text-xs text-blue-600 flex items-center gap-1">
            <span>ณ สิ้นวัน: {lastRow ? lastRow.date : '-'}</span>
          </div>
        </div>
      </div>

      {/* Balance Verification Bar */}
      {calculatedBalance !== null && closingStock !== null && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Scale className="w-4 h-4 text-slate-500" />
            <span className="font-semibold text-slate-700">สมดุลสต็อกเคมี:</span>
            <span>
              ยกยอดมา ({formatNum(prevStock)}) + รับ ({formatNum(totalIncome)}) - ใช้ ({formatNum(totalUsage)}) ={' '}
              <strong className="text-slate-800">{formatNum(calculatedBalance)} ตัน</strong>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span>Stock รายงานจริง: <strong className="text-blue-700">{formatNum(closingStock)} ตัน</strong></span>
            {Math.abs(calculatedBalance - closingStock) < 0.05 ? (
              <span className="text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded font-semibold text-[11px]">
                ✓ ยอดสมดุลตรงกัน
              </span>
            ) : (
              <span className="text-amber-700 bg-amber-100 px-2 py-0.5 rounded font-semibold text-[11px]">
                ส่วนต่าง: {formatNum(Math.abs(calculatedBalance - closingStock))} ตัน
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
